import { useCallback, useEffect, useState } from 'react';
import { Copy, CheckCheck } from 'lucide-react';
import { TutorialChecklist, type StepState, type TutorialStep } from '../../components/ui/TutorialChecklist';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CodeSnippet({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex items-center justify-between gap-2 bg-ui-bg border border-ui-border rounded-md px-3 py-2 font-mono">
      <span className="text-xs text-ui-text truncate">{value}</span>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 text-ui-text-subtle hover:text-ui-text transition-colors"
        title="Copiar"
      >
        {copied ? <CheckCheck size={13} className="text-green-400" /> : <Copy size={13} />}
      </button>
    </div>
  );
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol className="space-y-1.5 pl-4 list-decimal marker:text-ui-text-subtle">
      {items.map((item, i) => (
        <li key={i} className="text-sm text-ui-text-muted">
          {item}
        </li>
      ))}
    </ol>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
  siteUrl: string;  // e.g. "https://tusitio.com"
  tenantId: string; // used to scope localStorage key
}

// ─── Step definitions ─────────────────────────────────────────────────────────

function buildSteps(siteUrl: string): TutorialStep[] {
  const sitemapUrl = `${siteUrl.replace(/\/$/, '')}/sitemap.xml`;

  return [
    {
      id: 'create-account',
      title: 'Abre Google Search Console',
      body: (
        <p>
          Google Search Console es la herramienta gratuita de Google para saber si tu sitio
          aparece en los resultados de búsqueda. Si ya tienes una cuenta de Google (Gmail),
          puedes entrar directamente — no necesitas crear nada nuevo.
        </p>
      ),
      action: {
        label: 'Ir a Google Search Console',
        href: 'https://search.google.com/search-console',
      },
      doneLabel: 'Ya entré',
    },

    {
      id: 'add-property',
      title: 'Agrega tu sitio',
      body: (
        <Steps
          items={[
            'Haz clic en "Añadir propiedad" (botón en la esquina superior izquierda).',
            `Escribe la dirección de tu sitio: ${siteUrl}`,
            'Elige la opción "Prefijo de URL" y haz clic en Continuar.',
          ]}
        />
      ),
      doneLabel: 'Ya agregué mi sitio',
    },

    {
      id: 'verify',
      title: 'Demuéstrale a Google que el sitio es tuyo',
      variantPrompt: '¿Cómo quieres verificarlo?',
      variants: [
        {
          key: 'meta-tag',
          label: 'Código en mi sitio (recomendado)',
          body: (
            <div className="space-y-3">
              <p>
                Google te dará un código parecido a este (el tuyo será diferente):
              </p>
              <CodeSnippet value='<meta name="google-site-verification" content="abc123..." />' />
              <Steps
                items={[
                  'Copia el código que te da Google.',
                  'En el panel, ve a Ajustes → SEO.',
                  'Pega el código en el campo "Verificación de Google".',
                  'Haz clic en Guardar y luego publica el sitio.',
                  'Vuelve a Search Console y haz clic en Verificar.',
                ]}
              />
            </div>
          ),
        },
        {
          key: 'html-file',
          label: 'Archivo de verificación',
          body: (
            <div className="space-y-3">
              <p>
                Google te pedirá descargar un pequeño archivo y colocarlo en tu sitio.
                Dado que este sitio está administrado por la plataforma, contáctanos y
                lo configuramos por ti en minutos.
              </p>
            </div>
          ),
        },
        {
          key: 'google-analytics',
          label: 'Google Analytics (si ya lo tienes)',
          body: (
            <p>
              Si ya conectaste Google Analytics a este sitio, Search Console puede
              reconocerlo automáticamente. Selecciona esa opción en la pantalla de
              verificación de Google y sigue las instrucciones.
            </p>
          ),
        },
      ],
      doneLabel: 'Mi sitio está verificado',
    },

    {
      id: 'sitemap',
      title: 'Envía el mapa de tu sitio a Google',
      body: (
        <div className="space-y-3">
          <p>
            El mapa del sitio le dice a Google exactamente qué páginas existen. Tu sitio
            ya tiene uno generado automáticamente:
          </p>
          <CodeSnippet value={sitemapUrl} />
          <Steps
            items={[
              'En Search Console, abre el menú izquierdo y haz clic en "Sitemaps".',
              'Pega la URL de arriba en el campo "Añadir un nuevo sitemap".',
              'Haz clic en Enviar.',
            ]}
          />
        </div>
      ),
      action: {
        label: 'Abrir Search Console → Sitemaps',
        href: `https://search.google.com/search-console/sitemaps?resource_id=${encodeURIComponent(siteUrl)}`,
      },
      doneLabel: 'Ya envié el sitemap',
    },

    {
      id: 'wait-index',
      title: 'Espera a que Google encuentre tu sitio',
      body: (
        <div className="space-y-3">
          <p>
            Google tarda entre unos días y dos semanas en visitar tu sitio por primera vez.
            No hay nada más que hacer — solo esperar.
          </p>
          <p>
            Cuando esté listo, verás datos de clics e impresiones en el panel principal de
            Search Console. Puedes marcar este paso ahora y volver en unos días a revisarlo.
          </p>
        </div>
      ),
      skippable: false,
      doneLabel: 'Entendido, lo revisaré después',
    },
  ];
}

// ─── Component ───────────────────────────────────────────────────────────────

const STORAGE_KEY = (tenantId: string) => `gsc-tutorial-${tenantId}`;

export default function SearchConsoleTutorial({ siteUrl, tenantId }: Props) {
  const steps = buildSteps(siteUrl);

  const [initialState, setInitialState] = useState<Record<string, StepState>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY(tenantId));
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const handleStateChange = useCallback(
    (state: Record<string, StepState>) => {
      try {
        localStorage.setItem(STORAGE_KEY(tenantId), JSON.stringify(state));
      } catch {
        // localStorage might be unavailable in some contexts
      }
      setInitialState(state);
    },
    [tenantId],
  );

  // Re-seed if tenantId changes (e.g. admin switching tenants)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY(tenantId));
      setInitialState(raw ? JSON.parse(raw) : {});
    } catch {
      setInitialState({});
    }
  }, [tenantId]);

  return (
    <TutorialChecklist
      title="Conecta tu sitio a Google"
      subtitle="Sigue estos pasos para que tu sitio aparezca en los resultados de búsqueda."
      steps={steps}
      initialState={initialState}
      onStateChange={handleStateChange}
      completionTitle="¡Tu sitio está conectado a Google!"
      completionBody={
        <span>
          En unos días comenzarás a ver datos en Google Search Console. Recuerda
          publicar cualquier cambio en el sitio para que Google lo detecte más rápido.
        </span>
      }
    />
  );
}
